<template>
  <div>
    <div v-if="variety === 'heading'">
      <h2 v-if="headingTypeString === 2" class="p-postEditor__heading2">
        {{ item.itemableAttributes.content }}
      </h2>
      <h3 v-else-if="headingTypeString === 3" class="p-postEditor__heading3">
        {{ item.itemableAttributes.content }}
      </h3>
      <h4 v-else-if="headingTypeString === 4" class="p-postEditor__heading4">
        {{ item.itemableAttributes.content }}
      </h4>
    </div>
    <div v-else-if="variety === 'text'">
      <p class="p-postEditor__text" v-html="content"></p>
    </div>
    <div v-else-if="variety === 'point'" class="p-postEditor__point">
      <div class="p-postEditor__point__block">
        <p class="p-postEditor__point__heading">POINT</p>
        <p class="p-postEditor__point__title">
          {{ title }}
        </p>
        <p class="p-postEditor__point__desc">
          {{ content }}
        </p>
      </div>
    </div>
    <div v-else-if="variety === 'image'" class="p-postEditor__image">
      <v-row>
        <v-col lg="7">
          <v-img :src="itemImageSrc" class="lighten-2" max-height="300" contain>
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate color="grey lighten-5" />
              </v-row>
            </template>
          </v-img>
          <div v-if="quoteUrl" class="caption ma-1">
            引用元:
            <a
              class="d-inline"
              :href="quoteUrl"
              rel="noopener noreferrer"
              target="_blank"
            >
              {{ quoteUrl }}
            </a>
          </div>
        </v-col>
        <v-col lg="5">
          <h3
            v-if="item.itemableAttributes.heading"
            class="p-postEditor__heading3"
          >
            {{ item.itemableAttributes.heading }}
          </h3>
          <p v-if="content" class="p-postEditor__text">
            {{ content }}
          </p>
        </v-col>
      </v-row>
    </div>
    <div
      v-else-if="variety === 'external_link'"
      class="p-postEditor__externalLink"
    >
      <v-card>
        <v-card-title class="subtitle-2">
          <a :href="url" rel="noopener noreferrer" target="_blank">
            <v-icon class="mr-1"> mdi-launch </v-icon>
            {{ item.itemableAttributes.sentence }}
          </a>
        </v-card-title>
        <v-card-text>
          <p class="caption">
            {{ item.itemableAttributes.description }}
          </p>
        </v-card-text>
      </v-card>
    </div>
    <div v-else-if="variety === 'list'">
      <ul
        v-if="style === 'unordered' || style === 'un_ordered'"
        class="p-postEditor__unorderedList"
      >
        <p class="p-postEditor__unorderedList--title">
          {{ title }}
        </p>
        <li
          v-for="(listItem, index) in item.itemableAttributes.items"
          :key="index"
        >
          {{ listItem }}
        </li>
      </ul>
      <ol v-if="style === 'ordered'" class="p-postEditor__orderedList">
        <p class="p-postEditor__orderedList--title">
          {{ title }}
        </p>
        <li
          v-for="(listItem, index) in item.itemableAttributes.items"
          :key="index"
        >
          <span>{{ index + 1 }}</span>
          {{ listItem }}
        </li>
      </ol>
    </div>
    <div v-else-if="variety === 'quotation'">
      <blockquote class="p-postEditor__quotation">
        <v-icon class="ml-2 mt-1"> mdi-format-quote-open </v-icon>
        <div class="p-postEditor__quotation__block">
          <p class="p-postEditor__quotation__text">
            {{ content }}
          </p>
          <div class="text-right">
            <v-icon class="mb-1"> mdi-format-quote-close </v-icon>
          </div>
          <div v-if="sourceUrl" class="text-right ma-2">
            引用元 :
            <a class="p-postEditor__quotation__reference" :href="sourceUrl">
              <span class="p-postEditor__quotation__pink">{{ source }}</span>
            </a>
          </div>
          <div v-else class="text-right ma-2">
            引用元 :
            <span class="p-postEditor__quotation__pink">{{ source }}</span>
          </div>
        </div>
      </blockquote>
    </div>
    <div
      v-else-if="variety === 'internal_link' && targetPost"
      class="p-postEditor__internalLink"
    >
      <p class="p-postEditor__internalLink__heading">関連記事</p>
      <v-container fluid class="pa-2">
        <v-row>
          <v-col lg="2">
            <v-avatar class="profile" size="100" tile>
              <v-img :src="eyecatchableSrc(targetPost)" />
            </v-avatar>
          </v-col>
          <v-col lg="10">
            <div class="subtitle-2 pb-2">
              {{ targetPost.title }}
            </div>
            <p class="caption">
              {{ targetPost.introduction }}
            </p>
          </v-col>
        </v-row>
      </v-container>
    </div>
    <div v-else-if="variety === 'embedding'">
      <div
        class="embedding-content"
        v-html="hostConverter(item.itemableAttributes.data.html)"
      >
        <div class="embedding-content__description">
          <p>{{ item.itemableAttributes.description }}</p>
        </div>
      </div>
    </div>
    <div
      v-else-if="variety === 'product_introduction'"
      class="p-postEditor__product"
    >
      <div class="p-postEditor__product__main">
        <div class="p-postEditor__product__left">
          <div class="p-postEditor__product__figure">
            <img
              v-if="item.itemableAttributes.productImageDirectUrl"
              :src="
                imageSrc(
                  item.itemableAttributes.productImageDirectUrl,
                  200,
                  200
                )
              "
            />
            <img
              v-else-if="item.itemableAttributes.downloadImageUrl"
              :src="
                imageSrc(item.itemableAttributes.downloadImageUrl, 200, 200)
              "
            />
          </div>
          <div v-if="quoteUrl">
            出典：
            <a
              class="p-postEditor__product__quoteUrl"
              :href="quoteUrl"
              target="_blank"
            >
              {{ quoteUrl }}
            </a>
          </div>
        </div>
        <div class="p-postEditor__product__right">
          <p>{{ item.itemableAttributes.alternateName }}</p>
          <h3 class="p-postEditor__product__heading">
            {{ item.itemableAttributes.name }}
          </h3>
          <h4 v-if="item.itemableAttributes.price">
            <span class="p-postEditor__product__price"
            >{{ item.itemableAttributes.price }}円</span
            >
            （
            <span v-if="item.itemableAttributes.taxIncluded"> 税込 </span>
            <span v-else> 税抜 </span>
            ・参考価格）
          </h4>
          <v-btn
            v-if="item.itemableAttributes.amazonUrl"
            :href="item.itemableAttributes.amazonUrl"
            target="_blank"
            class="mt-2 mb-1 pa-2"
            tile
            outlined
            small
            min-width="200"
            color="orange darken-2"
          >
            <v-icon left small class="ml-1 mr-2"> mdi-amazon </v-icon>
            Amazonで詳細を見る
          </v-btn>
          <v-btn
            v-if="item.itemableAttributes.rakutenUrl"
            :href="item.itemableAttributes.rakutenUrl"
            target="_blank"
            class="mt-2 mb-1 pa-2"
            tile
            outlined
            small
            min-width="200"
            color="red darken-4"
          >
            <v-icon small class="ml-1 mr-2"></v-icon>
            楽天で詳細を見る
          </v-btn>
          <v-btn
            v-if="item.itemableAttributes.officialUrl"
            :href="item.itemableAttributes.officialUrl"
            target="_blank"
            class="mt-2 my-1 pa-2"
            tile
            outlined
            small
            min-width="200"
            color="primary"
          >
            公式サイトで詳細を見る
          </v-btn>
          <v-btn
            v-if="item.itemableAttributes.otherUrl"
            :href="item.itemableAttributes.otherUrl"
            target="_blank"
            class="mt-2 my-1 pa-2"
            tile
            outlined
            small
            min-width="200"
            color="indigo"
          >
            {{
              item.itemableAttributes.otherUrlSentence || '商品の詳細を見る'
            }}
          </v-btn>
        </div>
      </div>
      <div class="p-postEditor__product__footer">
        <h4 class="p-postEditor__product__intro_heading"></h4>
        <p
          class="p-postEditor__product__introduction"
          style="white-space: pre-line"
        >
          {{ item.itemableAttributes.description }}
        </p>
      </div>
    </div>
    <div v-else-if="variety === 'html'">
      <div class="html-content" style="padding:30px 0">
        <div v-html="item.itemableAttributes.content" ></div>
      </div>
    </div>
    <div v-else-if="variety === 'character'">
      <div class="p-characterItem">
        <div
          v-if="item.itemableAttributes.imageSide === 'right'"
          class="p-characterItem__rightBalloon"
        >
          <div class="p-characterItem__rightBalloon__content">
            <span class="p-characterItem__contentBody">{{ content }}</span>
          </div>
        </div>
        <div class="p-characterItem__avatar">
          <v-avatar size="72">
            <img
              :src="item.itemableAttributes.characterImage.avatar.thumb.url"
            />
          </v-avatar>
          <div class="mt-1">
            {{ item.itemableAttributes.characterImage.name }}
          </div>
        </div>
        <div
          v-if="item.itemableAttributes.imageSide === 'left'"
          class="p-characterItem__leftBalloon"
        >
          <div class="p-characterItem__leftBalloon__content">
            <span class="p-characterItem__contentBody">{{ content }}</span>
          </div>
        </div>
      </div>

      <div class="html-content"></div>
    </div>
    <div v-else-if="variety === 'table_item'">
      <div v-if="item.itemableAttributes.tableTitle" class="tableTitle">
        <span class="tableTitle--text">{{
            item.itemableAttributes.tableTitle
          }}</span>
      </div>
      <table class="table">
        <tr
          v-for="(row, rowIndex) in item.itemableAttributes.tableContent"
          :key="rowIndex"
        >
          <td
            v-for="(col, colIndex) in item.itemableAttributes.tableContent[
              rowIndex
            ]"
            :key="colIndex"
            class="table-cell"
            :style="cellStyle(colIndex, rowIndex)"
          >
            <div
              class="table-div"
              v-html="
                returnFilter(
                  item.itemableAttributes.tableContent[rowIndex][colIndex]
                )
              "
            ></div>
          </td>
        </tr>
      </table>
    </div>
    <div v-else-if="variety === 'button'" class='pt-3'>
      <ButtonItemPreview :item='item'/>
    </div>
    <div v-else-if="variety === 'banner'" class='pt-3'>
      <BannerItemPreview :item='item'/>
    </div>
  </div>
</template>

<script>
import imageUtilsMixin from '@/mixins/image_utils'
import ButtonItemPreview from '../items/previews/ButtonItemPreview'
import BannerItemPreview from '../items/previews/BannerItemPreview'

export default {
  name: 'ItemPreview',
  components: { BannerItemPreview, ButtonItemPreview },
  mixins: [imageUtilsMixin],
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  data: function () {
    return {}
  },
  computed: {
    variety: function () {
      return this.item.variety
    },
    title: function () {
      return this.item.itemableAttributes.title
    },
    content: function () {
      return this.item.itemableAttributes.content
    },
    headingTypeString: function () {
      const headingTypeString = this.item.itemableAttributes.headingType
      if (typeof headingTypeString === 'number') {
        return headingTypeString
      } else {
        return Number(headingTypeString)
      }
    },
    quoteUrl: function () {
      return this.item.itemableAttributes.quoteUrl
    },
    url: function () {
      return this.item.itemableAttributes.url
    },
    style: function () {
      return this.item.itemableAttributes.style
    },
    source: function () {
      return this.item.itemableAttributes.source
    },
    sourceUrl: function () {
      return this.item.itemableAttributes.sourceUrl
    },
    targetPost: function () {
      return this.item.itemableAttributes.targetPost
    },
    eyecatchableSrc () {
      return function (post) {
        if (post.eyecatchable) {
          return post.eyecatchable.file.middle.url
        } else {
          return 'http://placehold.jp/eeeeee/cccccc/200x200.png?text=No%20Image'
        }
      }
    },
    html: function () {
      return this.item.itemableAttributes.html
    },
    itemImageSrc: function () {
      if (this.item.itemableAttributes.imageDirectUrl) {
        return this.item.itemableAttributes.imageDirectUrl
      } else {
        return this.item.itemableAttributes.imagableUrl
      }
    }
  },
  mounted () {
    this.runScript()
    this.reloadInstagramScript()
  },
  updated () {
    this.runScript()
    this.reloadInstagramScript()
  },
  methods: {
    runScript () {
      const scripts = this.$el.querySelectorAll('script')
      scripts.forEach((script) => {
        const parentNode = script.parentNode
        let alternativeNode
        // todo: ホワイトリスト方式にする
        if (script.src.indexOf('https://gist.github.com/') !== -1) {
          alternativeNode = document.createElement('iframe')
          alternativeNode.src = URL.createObjectURL(
            new Blob(['<!DOCTYPE html><title></title>' + script.outerHTML], {
              type: 'text/html'
            })
          )
          alternativeNode.onload = () => {
            alternativeNode.height =
              alternativeNode.contentDocument.body.scrollHeight + 50
          }
        } else {
          alternativeNode = document.createElement('script')
          alternativeNode.src = script.src
        }
        parentNode.replaceChild(alternativeNode, script)
      })
    },
    reloadInstagramScript () {
      if (window.instgrm) {
        window.instgrm.Embeds.process()
      }
    },
    returnFilter (val) {
      return val.replace(/\r?\n/g, '<br />')
    },
    hostConverter (html) {
      return html.replace(
        /brownweasel1.sakura.ne.jp/g,
        'activel.image-storage-b-cms.com'
      )
    },
    cellStyle: function (colIndex, rowIndex) {
      // 横幅設定
      let style = 'width: '
      if (this.item.itemableAttributes.tableContent) {
        style += (
          100 / this.item.itemableAttributes.tableContent[0].length
        ).toString()
      } else {
        style += '100'
      }
      style += '%;'
      // 文字位置設定
      if (this.item.itemableAttributes.tableStyle) {
        let tmpobj = this.item.itemableAttributes.tableStyle
        Object.keys(tmpobj).forEach(function (key) {
          if (colIndex === Number(key)) {
            style += 'text-align: ' + tmpobj[key] + ';'
          }
        })
      }
      // 背景色設定
      if (this.item.itemableAttributes.headerStyle) {
        let obj = this.item.itemableAttributes.headerStyle
        let STRIPE_HEADER_COLOR = '#f7f7f7'
        if (obj['row'] && Number(rowIndex) === 0) {
          if (obj['row'] === 'stripe') {
            style += 'background-color: ' + STRIPE_HEADER_COLOR + ';'
          } else {
            style += 'background-color: white;'
          }
        }
        if (obj['col'] && Number(colIndex) === 0) {
          if (obj['col'] === 'stripe') {
            style += 'background-color: ' + STRIPE_HEADER_COLOR + ';'
          }
        }
      }
      return style
    }
  }
}
</script>

<style scoped lang="sass">
.p-characterItem
  display: flex
  &__contentBody
    white-space: pre-line
    word-wrap: break-word
  &__avatar
    width: 120px
    text-align: center
  &__leftBalloon
    padding: 10px 0
    &__content
      display: inline-block
      position: relative
      margin-left: 5px
      padding: 17px 13px
      border-radius: 12px
      background: #e8e8e8
      min-width: 400px
      &:after
        content: ""
        display: inline-block
        position: absolute
        top: 18px
        left: -24px
        border: 12px solid transparent
        border-right: 12px solid #e8e8e8
  &__rightBalloon
    padding: 10px 0
    &__content
      display: inline-block
      position: relative
      margin-right: 5px
      padding: 17px 13px
      border-radius: 12px
      background: #e8e8e8
      min-width: 400px
      &:after
        content: ""
        display: inline-block
        position: absolute
        top: 18px
        right: -24px
        border: 12px solid transparent
        border-right: 12px solid #e8e8e8
        -webkit-transform: rotate(-180deg)
        transform: rotate(-180deg)
.tableTitle
  text-align: center
  margin-bottom: 12px
  font-weight: bold
  &--text
    border-bottom: 1px solid #f37f77
    padding-bottom: 0.2em
.table
  width: 100%
  table-layout: fixed
  border: 1px double #B4B4B4
.table-cell
  border: 1px solid #e8e8e8
  width: 100%
  padding: 0.5rem
.table-div
  min-height: 1.5rem
</style>

<style lang="sass">
.html-content
  iframe[title="YouTube video player"]
    max-width: 500px !important
    max-height: 350px !important
</style>
